// Invoices: upload, read with Claude, match to budget lines
import { CUR, numOf } from '../core/budget.js';
import { useEffect, useMemo, useRef, useState } from '../core/react.js';
import { MONO, T, fmt } from '../core/theme.js';
import { Pill } from '../ui/widgets.js';
import { Icon } from '../ui/icons.js';
import { useNarrow } from '../ui/narrow.js';
import { safeFileUrl } from '../core/util.js';
import { money, numStr, suggestLine, takeDocument } from '../core/doc-upload.js';
import { DocPreview, DropCard, InvLabel, ListHead, NoProject, ReadingCard, invInput } from '../ui/doc-review.js';
import { isoToday } from '../core/dates.js';
import { CostEditor } from './cost-edit.js';
import { costsSummary } from '../core/costs-summary.js';

const INV_STATUSES = ['Received', 'Approved', 'Paid', 'Void'];
const INV_ST = {
  Received: {
    color: T.blue,
    bg: T.blueBg,
    br: T.blueBr
  },
  Approved: {
    color: T.amber,
    bg: T.amberBg,
    br: T.amberBr
  },
  Paid: {
    color: T.green,
    bg: T.greenBg,
    br: T.greenBr
  },
  Void: {
    color: T.mist,
    bg: T.ivoryD,
    br: T.ivoryDD
  }
};
export function Invoices({
  project,
  sections,
  fx,
  user,
  costAction,
  reloadBudget,
  flashSync,
  setView
}) {
  const hasProject = !!project;
  const [invoices, setInvoices] = useState([]);
  const [listState, setListState] = useState('idle');
  const [listErr, setListErr] = useState('');
  const [stage, setStage] = useState('list');
  const [file, setFile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState(null);
  const [allocs, setAllocs] = useState([]);
  const [markInv, setMarkInv] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // phones have no dragging, and the panes are stacked: the add panel is above the list, not beside it
  const narrow = useNarrow();
  const fileInput = useRef(null);
  const items = useMemo(() => sections.flatMap(s => s.items.map(it => ({
    ...it,
    secName: s.name
  }))), [sections]);
  const itemById = useMemo(() => Object.fromEntries(items.map(it => [it.id, it])), [items]);
  const invoicedByLine = useMemo(() => {
    const m = {};
    invoices.forEach(inv => {
      if (inv.status === 'Void') return;
      inv.allocations.forEach(a => {
        m[a.lineId] = (m[a.lineId] || 0) + (Number(a.netLine) || 0);
      });
    });
    return m;
  }, [invoices]);
  async function loadList() {
    if (!hasProject) return;
    setListState('loading');
    const res = await costAction('listInvoices', {});
    if (res.ok) {
      setInvoices(res.invoices || []);
      setListState('idle');
    } else {
      setListErr(res.error);
      setListState('error');
    }
  }
  useEffect(() => {
    loadList();
  }, [project?.id, hasProject]);
  useEffect(() => () => {
    if (file?.url) URL.revokeObjectURL(file.url);
  }, [file]);
  useEffect(() => {
    const stop = e => {
      if (Array.from(e.dataTransfer?.types || []).includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => {
      window.removeEventListener('dragover', stop);
      window.removeEventListener('drop', stop);
    };
  }, []);
  function discard() {
    setFile(null);
    setDraft(null);
    setNote(null);
    setAllocs([]);
    setStage('list');
  }
  async function handleFile(f) {
    const doc = await takeDocument(f, {
      flash: flashSync,
      onStart: file => { setFile(file); setStage('reading'); setNote(null); },
      // photos are kept as the same compressed copy Claude reads, so the file is stored once
      onShrunk: patch => setFile(cur => cur && Object.assign(cur, patch))
    });
    if (!doc) return;
    const res = await costAction('extractInvoice', { fileData: doc.b64, mediaType: doc.type });
    const x = res.ok ? res.invoice || {} : {};
    const d = {
      supplier: String(x.supplier_name ?? ''),
      taxId: String(x.supplier_tax_id ?? ''),
      number: String(x.invoice_number ?? ''),
      date: String(x.invoice_date ?? ''),
      due: String(x.due_date ?? ''),
      ccy: String(x.currency ?? '').toUpperCase(),
      net: numStr(x.net),
      vat: numStr(x.vat),
      gross: numStr(x.gross),
      description: String(x.description ?? ''),
      notes: String(x.notes ?? ''),
      confidence: x.confidence || null,
      partial: !!x.is_partial,
      kind: x.document_type === 'receipt' ? 'receipt' : 'invoice',
      method: String(x.payment_method ?? ''),
      paidBy: x.document_type === 'receipt' ? user?.name || '' : ''
    };
    setDraft(d);
    if (!res.ok) setNote({
      type: 'err',
      text: `It couldn't be read automatically (${res.error}). Fill in the fields from the preview.`
    });else if (x.notes || x.confidence === 'low') setNote({
      type: 'warn',
      text: x.notes || 'Low-confidence read. Check every field against the preview.'
    });
    const sug = suggestLine(sections, d.supplier);
    setAllocs([{
      key: 'a' + Date.now(),
      itemId: sug?.id || '',
      amount: d.gross
    }]);
    setStage('review');
  }
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (stage === 'list') handleFile(e.dataTransfer.files?.[0]);
  }
  // A cost typed in by hand, with no document (cash, a lost receipt, a card payment)
  function manual() {
    setFile(null);
    setNote(null);
    setDraft({
      supplier: '', taxId: '', number: '', date: isoToday(), due: '',
      ccy: items[0]?.ccy || CUR.cost, net: '', vat: '', gross: '', description: '', notes: '',
      confidence: null, partial: false, kind: 'receipt', method: 'card', paidBy: user?.name || ''
    });
    setAllocs([{ key: 'a' + Date.now(), itemId: '', amount: '' }]);
    setStage('review');
  }
  const g = draft ? numOf(draft.gross) : NaN;
  const n = draft ? numOf(draft.net) : NaN;
  const v = draft ? numOf(draft.vat) : NaN;
  const netEff = isFinite(n) ? n : isFinite(v) && isFinite(g) ? g - v : g;
  const vatEff = isFinite(v) ? v : isFinite(g) && isFinite(netEff) ? g - netEff : 0;
  const netRatio = g > 0 && isFinite(netEff) ? netEff / g : 1;
  const arithOff = isFinite(g) && isFinite(n) && isFinite(v) && Math.abs(n + v - g) > 0.02;
  const ccyKnown = !!(draft && fx[draft.ccy]);
  const allocTotal = allocs.reduce((s, a) => s + (numOf(a.amount) || 0), 0);
  const remaining = isFinite(g) ? g - allocTotal : NaN;
  const rateTo = it => ccyKnown && fx[it.ccy] ? fx[draft.ccy].gbp / fx[it.ccy].gbp : NaN;
  function setField(k, val) {
    setDraft(d => ({
      ...d,
      [k]: val
    }));
  }
  function setAlloc(key, patch) {
    setAllocs(prev => prev.map(a => a.key === key ? {
      ...a,
      ...patch
    } : a));
  }
  function addAlloc() {
    setAllocs(prev => [...prev, {
      key: 'a' + Date.now(),
      itemId: '',
      amount: remaining > 0.005 ? remaining.toFixed(2) : ''
    }]);
  }
  async function save(force = false) {
    const missing = [];
    if (!draft.supplier.trim()) missing.push(draft.kind === 'receipt' ? 'who was paid' : 'supplier');
    if (!(g > 0)) missing.push('gross total');
    if (!ccyKnown) missing.push(`an FX rate for ${draft.ccy || 'its currency'} (Settings)`);
    // one line and no amount typed: the whole total goes to that line
    const useAllocs = allocs.length === 1 && !(numOf(allocs[0].amount) > 0) && g > 0 ? [{ ...allocs[0], amount: String(g) }] : allocs;
    const rows = useAllocs.filter(a => a.itemId && numOf(a.amount) > 0);
    if (!rows.length) missing.push('a budget line with an amount');
    if (missing.length) {
      flashSync('Still needed: ' + missing.join(', '), 'err');
      return;
    }
    if (allocTotal - g > 0.01) {
      flashSync('The lines add up to more than the total', 'err');
      return;
    }
    const allocations = rows.map(a => {
      const it = itemById[a.itemId];
      const noVatSplit = draft.kind === 'receipt' && !isFinite(n) && !isFinite(v);
      const gross = numOf(a.amount),
        net = noVatSplit ? gross / (1 + (Number(it?.vat) || 0)) : gross * netRatio,
        rate = it ? rateTo(it) : NaN;
      return {
        row: it?._sheetRow,
        desc: it?.desc,
        ln: it?.ln,
        lineCcy: it?.ccy,
        gross,
        net,
        fx: rate,
        netLine: net * rate
      };
    });
    if (allocations.some(a => !a.row)) {
      flashSync('A selected budget line has no line number yet. Reload the budget and try again.', 'err');
      return;
    }
    if (allocations.some(a => !isFinite(a.fx))) {
      flashSync('A selected line uses a currency with no FX rate. Add it in Settings.', 'err');
      return;
    }
    setSaving(true);
    const res = await costAction('saveInvoice', {
      invoice: {
        supplier: draft.supplier.trim(),
        taxId: draft.taxId.trim(),
        number: String(draft.number).trim(),
        date: draft.date,
        due: draft.due,
        ccy: draft.ccy,
        net: netEff,
        vat: vatEff,
        gross: g,
        description: draft.description.trim(),
        notes: draft.notes.trim(),
        kind: draft.kind,
        paidBy: (draft.paidBy || '').trim(),
        method: draft.method || ''
      },
      allocations,
      markInvoiced: markInv && draft.kind !== 'receipt',
      force,
      fileData: file?.b64,
      mediaType: file?.type,
      addedBy: user?.name || ''
    });
    setSaving(false);
    if (res.duplicate) {
      if (window.confirm(`${draft.kind === 'receipt' ? 'Receipt' : 'Invoice'} ${draft.number} from ${draft.supplier} is already recorded as ${res.existingId}. Attach it again anyway?`)) return save(true);
      return;
    }
    if (!res.ok) {
      flashSync(`Not attached: ${res.error}`, 'err');
      return;
    }
    flashSync(`${res.id} attached to ${allocations.length} budget line${allocations.length > 1 ? 's' : ''}`);
    discard();
    await reloadBudget();
    loadList();
  }
  // correcting a cost already added, or deleting one added by mistake (it goes to the bin on Activity & restore)
  const [editing, setEditing] = useState(null);
  const canChange = user?.role !== 'Read-only';
  async function saveEdit(inv, invoice) {
    const res = await costAction('updateInvoice', { id: inv.id, invoice });
    if (!res.ok) { flashSync(`Not saved: ${res.error}`, 'err'); return false; }
    flashSync(res.unchanged ? 'Nothing changed' : `${inv.supplier || inv.id} updated`);
    setEditing(null); loadList();
    return true;
  }
  async function deleteCost(inv) {
    if (!window.confirm(`Delete ${inv.kind === 'receipt' ? 'the receipt' : 'invoice ' + (inv.number || inv.id)} from ${inv.supplier || 'this supplier'}? Its amounts come off the budget. It stays in the bin on Activity & restore, where it can be restored.`)) return;
    const res = await costAction('deleteInvoice', { id: inv.id });
    if (!res.ok) { flashSync(`Not deleted: ${res.error}`, 'err'); return; }
    setInvoices(prev => prev.filter(x => x.id !== inv.id));
    if (editing === inv.id) setEditing(null);
    flashSync(`${inv.supplier || inv.id} deleted`);
  }
  async function changeStatus(inv, status) {
    if (status === inv.status) return;
    if (status === 'Void' && !window.confirm(`Void ${inv.id}? Its amounts come off the budget actuals.`)) return;
    const res = await costAction('setInvoiceStatus', {
      id: inv.id,
      status
    });
    if (!res.ok) {
      flashSync(`Status not changed: ${res.error}`, 'err');
      return;
    }
    setInvoices(prev => prev.map(x => x.id === inv.id ? {
      ...x,
      status
    } : x));
    flashSync(`${inv.id} → ${status}`);
    if (status === 'Void' || inv.status === 'Void') reloadBudget();
  }
  if (!hasProject) return React.createElement(NoProject, { text: 'Create or pick a project to record its costs.', setView });
  if (stage === 'review' && draft) {
    const confColor = {
      high: T.green,
      medium: T.amber,
      low: T.red
    }[draft.confidence];
    const fieldRow = (label, k, opts = {}) => React.createElement("div", {
      style: {
        gridColumn: opts.span ? `span ${opts.span}` : undefined
      }
    }, React.createElement(InvLabel, null, label), React.createElement("input", {
      type: opts.type || 'text',
      value: draft[k],
      onChange: e => setField(k, e.target.value),
      placeholder: opts.ph || '',
      inputMode: opts.num ? 'decimal' : undefined,
      style: invInput(opts.mono || opts.num, opts.num ? {
        textAlign: 'right'
      } : {})
    }));
    return React.createElement("div", {
      style: {
        display: 'flex',
        height: '100%',
        overflow: 'hidden'
      },
      className: "fade-up pane-row"
    }, React.createElement(DocPreview, { file, what: 'Invoice', basis: '46%', empty: React.createElement("div", {
      style: { padding: '60px 24px', textAlign: 'center', color: T.mist, fontSize: 12.5, lineHeight: 1.6 }
    }, "Entered by hand, with no document attached.", React.createElement("br"), "Add a short note so finance knows what it was.") }), React.createElement("div", {
      style: {
        width: 560,
        flexShrink: 0,
        background: T.white,
        overflowY: 'auto',
        padding: '24px 26px',
        display: 'flex',
        flexDirection: 'column',
        gap: 18
      }
    }, React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12
      }
    }, React.createElement("div", null, React.createElement("div", {
      style: {
        fontSize: 16,
        fontWeight: 600,
        color: T.coal,
        marginBottom: 3
      }
    }, "Check the details"), React.createElement("div", {
      style: {
        fontSize: 12,
        color: T.mist
      }
    }, "Correct anything that doesn't match the document, then choose the budget line.")), confColor && React.createElement(Pill, {
      label: `${draft.confidence} confidence`,
      color: confColor,
      bg: confColor + '18'
    })), note && React.createElement("div", {
      style: {
        padding: '10px 12px',
        fontSize: 12,
        lineHeight: 1.5,
        background: note.type === 'err' ? T.redBg : T.amberBg,
        color: note.type === 'err' ? T.red : T.amber,
        border: `1px solid ${note.type === 'err' ? T.redBr : T.amberBr}`
      }
    }, note.text), React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 12
      }
    }, React.createElement("div", {
      style: { gridColumn: 'span 3', display: 'flex', gap: 6 }
    }, [['invoice', 'Supplier invoice'], ['receipt', 'Receipt or payment already made']].map(([k, l]) => React.createElement("button", {
      key: k,
      onClick: () => setField('kind', k),
      style: { padding: '7px 12px', fontSize: 11.5, fontWeight: 600, border: `1px solid ${draft.kind === k ? T.navy : T.rule}`, background: draft.kind === k ? T.navy : T.white, color: draft.kind === k ? T.white : T.slate }
    }, l))), fieldRow(draft.kind === 'receipt' ? 'PAID TO' : 'SUPPLIER', 'supplier', {
      span: 2
    }), draft.kind === 'receipt' ? fieldRow('PAID BY', 'paidBy', {
      ph: 'Who paid'
    }) : fieldRow('TAX ID', 'taxId', {
      mono: true
    }), fieldRow(draft.kind === 'receipt' ? 'RECEIPT NO.' : 'INVOICE NO.', 'number', {
      mono: true
    }), fieldRow(draft.kind === 'receipt' ? 'DATE PAID' : 'INVOICE DATE', 'date', {
      type: 'date'
    }), draft.kind === 'receipt' ? React.createElement("div", null, React.createElement(InvLabel, null, "PAID WITH"), React.createElement("select", {
      value: draft.method || '',
      onChange: e => setField('method', e.target.value),
      style: { width: '100%', padding: '8px 10px', border: `1px solid ${T.rule}`, background: T.ivory, fontSize: 12.5, color: T.coal }
    }, [['card', 'Company card'], ['personal card', 'Personal card (reimburse)'], ['cash', 'Cash'], ['bank transfer', 'Bank transfer'], ['', 'Not known']].map(([v2, l]) => React.createElement("option", { key: v2, value: v2 }, l)))) : fieldRow('DUE DATE', 'due', {
      type: 'date'
    }), fieldRow('WHAT IT\'S FOR', 'description', {
      span: 3,
      ph: draft.kind === 'receipt' ? 'e.g. Taxis to venue, crew lunch' : 'e.g. Deposit – venue hire'
    })), React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: '90px 1fr 1fr 1fr',
        gap: 12,
        alignItems: 'end'
      }
    }, React.createElement("div", null, React.createElement(InvLabel, null, "CCY"), React.createElement("select", {
      value: draft.ccy,
      onChange: e => setField('ccy', e.target.value),
      style: invInput(false, {
        padding: '9px 6px'
      })
    }, !fx[draft.ccy] && React.createElement("option", {
      value: draft.ccy
    }, draft.ccy || '—'), Object.keys(fx).map(c => React.createElement("option", {
      key: c
    }, c)))), fieldRow('NET', 'net', {
      num: true
    }), fieldRow('VAT', 'vat', {
      num: true
    }), fieldRow('GROSS TOTAL', 'gross', {
      num: true
    })), !ccyKnown && draft.ccy && React.createElement("div", {
      style: {
        fontSize: 11.5,
        color: T.red,
        marginTop: -8
      }
    }, "No FX rate for ", draft.ccy, ". Add it in Settings or pick the right currency."), arithOff && React.createElement("div", {
      style: {
        fontSize: 11.5,
        color: T.amber,
        marginTop: -8
      }
    }, "Net + VAT is ", money(n + v), ", not ", money(g), ". Check the figures against the document."), React.createElement("div", {
      style: {
        borderTop: `1px solid ${T.rule}`,
        paddingTop: 16
      }
    }, React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 10
      }
    }, React.createElement("div", {
      style: {
        fontSize: 13,
        fontWeight: 600,
        color: T.coal
      }
    }, "Budget lines"), React.createElement("div", {
      style: {
        ...MONO,
        fontSize: 11,
        color: Math.abs(remaining) <= 0.01 ? T.green : T.amber
      }
    }, Math.abs(remaining) <= 0.01 ? `Fully allocated · ${money(g, draft.ccy)}` : remaining > 0 ? `${money(remaining, draft.ccy)} not yet allocated` : `${money(-remaining, draft.ccy)} over the total`)), allocs.map(a => {
      const it = itemById[a.itemId];
      const rate = it ? rateTo(it) : NaN;
      const thisLine = it ? (numOf(a.amount) || 0) * netRatio * rate * (1 + (it.vat || 0)) : NaN;
      const hasInv = it && it.lineId && invoicedByLine[it.lineId] != null;
      const soFar = hasInv ? invoicedByLine[it.lineId] * (1 + (it.vat || 0)) : 0;
      const budgetGross = it ? it.qty * it.up * (1 + (it.vat || 0)) : NaN;
      const after = soFar + thisLine;
      return React.createElement("div", {
        key: a.key,
        style: {
          marginBottom: 12
        }
      }, React.createElement("div", {
        style: {
          display: 'grid',
          gridTemplateColumns: '1fr 130px 28px',
          gap: 8
        }
      }, React.createElement("select", {
        value: a.itemId,
        onChange: e => setAlloc(a.key, {
          itemId: e.target.value
        }),
        style: invInput(false)
      }, React.createElement("option", {
        value: ""
      }, "Choose a budget line\u2026"), sections.map(sec => React.createElement("optgroup", {
        key: sec.id,
        label: sec.name
      }, sec.items.map(x => React.createElement("option", {
        key: x.id,
        value: x.id
      }, x.ln, ". ", x.desc, x.sup ? ` — ${x.sup}` : '', " (", x.ccy, ")"))))), React.createElement("input", {
        value: a.amount,
        onChange: e => setAlloc(a.key, {
          amount: e.target.value
        }),
        inputMode: "decimal",
        placeholder: "0.00",
        style: invInput(true, {
          textAlign: 'right'
        })
      }), React.createElement("button", {
        onClick: () => setAllocs(prev => prev.filter(x => x.key !== a.key)),
        title: "Remove line",
        disabled: allocs.length === 1,
        style: {
          opacity: allocs.length === 1 ? .25 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }
      }, React.createElement(Icon.trash, {
        s: 13,
        c: T.mist
      }))), it && isFinite(thisLine) && React.createElement("div", {
        style: {
          ...MONO,
          fontSize: 10.5,
          color: after > budgetGross * 1.0001 ? T.red : T.slate,
          marginTop: 5,
          lineHeight: 1.6
        }
      }, it.ccy !== draft.ccy && React.createElement(React.Fragment, null, "\u2248 ", money(thisLine, it.ccy), " at ", rate.toFixed(4), " \xB7 "), "Budget ", money(budgetGross, it.ccy), " \xB7 invoiced ", money(soFar, it.ccy), " \u2192 ", money(after, it.ccy), !hasInv && it.ag != null && React.createElement("div", {
        style: {
          color: T.amber
        }
      }, "Replaces the actual typed in by hand (", money(it.ag, it.ccy), ")")));
    }), React.createElement("button", {
      onClick: addAlloc,
      style: {
        fontSize: 11.5,
        color: T.navyL,
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 0'
      }
    }, React.createElement(Icon.plus, {
      s: 11,
      c: T.navyL
    }), " Split across another line")), draft.kind !== 'receipt' && React.createElement("label", {
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        color: T.slate,
        cursor: 'pointer'
      }
    }, React.createElement("input", {
      type: "checkbox",
      checked: markInv,
      onChange: e => setMarkInv(e.target.checked)
    }), "Set the line status to Invoiced"), React.createElement("div", {
      style: {
        display: 'flex',
        gap: 10,
        marginTop: 'auto',
        paddingTop: 6
      }
    }, React.createElement("button", {
      onClick: discard,
      disabled: saving,
      style: {
        padding: '12px 18px',
        border: `1px solid ${T.rule}`,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.1em',
        color: T.slate
      }
    }, "DISCARD"), React.createElement("button", {
      onClick: () => save(false),
      disabled: saving,
      style: {
        flex: 1,
        padding: '12px',
        background: T.navy,
        color: T.white,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.1em',
        opacity: saving ? .6 : 1
      }
    }, saving ? 'ATTACHING…' : 'ATTACH TO BUDGET'))));
  }
  const live = invoices.filter(i => i.status !== 'Void');
  const sum = costsSummary(invoices, sections, fx, 'usd');   // the same sums as the Dashboard's "Costs in", in the billing currency
  const who = x => `${x.supplier || 'A cost'}${x.number ? ' ' + x.number : ''}`;
  const lineLabel = a => `${a.ln ? a.ln + '. ' : ''}${a.desc}`;
  return React.createElement("div", {
    style: {
      display: 'flex',
      height: '100%',
      overflow: 'hidden',
      background: T.ivory
    },
    className: "fade-up pane-row",
    onDragOver: e => {
      e.preventDefault();
      if (stage === 'list') setDragOver(true);
    },
    onDragLeave: e => {
      if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
    },
    onDrop: onDrop
  }, React.createElement("div", {
    style: {
      width: 300,
      background: T.white,
      borderRight: `1px solid ${T.rule}`,
      padding: '26px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: 18,
      flexShrink: 0,
      overflowY: 'auto'
    }
  }, React.createElement("div", null, React.createElement("div", {
    style: {
      fontSize: 16,
      fontWeight: 600,
      color: T.coal,
      marginBottom: 4
    }
  }, "Add a cost"), React.createElement("div", {
    style: {
      fontSize: 12,
      color: T.mist,
      lineHeight: 1.5
    }
  }, narrow ? "Add a supplier invoice or take a photo of a receipt, and Claude reads it; you check it and choose the budget line. Or enter a cost by hand." : "Drop a supplier invoice or a receipt photo and Claude reads it; you check it and choose the budget line. Or enter a cost by hand.")), stage === 'reading' ? React.createElement(ReadingCard, { text: 'Reading it', file }) : React.createElement(DropCard, { icon: Icon.receipt, add: 'Add an invoice or receipt', drop: 'Drop an invoice or receipt', dragOver, narrow, inputRef: fileInput, onFile: handleFile }), stage !== 'reading' && React.createElement("button", {
    onClick: manual,
    style: { padding: '9px', border: `1px solid ${T.rule}`, background: T.white, fontSize: 12, color: T.coal, fontWeight: 500 }
  }, "Enter a cost by hand"), React.createElement("div", {
    style: {
      borderTop: `1px solid ${T.rule}`,
      paddingTop: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 8
    }
  }, React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 12,
      color: T.slate
    }
  }, React.createElement("span", null, "Costs on this budget"), React.createElement("span", {
    style: {
      ...MONO,
      color: T.coal
    }
  }, live.length)), React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 12,
      color: T.slate
    }
  }, React.createElement("span", null, "Total before tax"), React.createElement("span", {
    style: {
      ...MONO,
      color: T.coal
    }
  }, "\u2248 ", fmt.usd(sum.net))), React.createElement("div", {
    style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.slate }
  }, React.createElement("span", null, "Total including tax"), React.createElement("span", {
    style: { ...MONO, color: T.coal }
  }, "\u2248 ", fmt.usd(sum.gross))), React.createElement("div", {
    style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.slate }
  }, React.createElement("span", { title: 'Invoices marked Paid, and receipts' }, `Paid, before tax (${sum.paidCount} of ${sum.count})`), React.createElement("span", {
    style: { ...MONO, color: T.coal }
  }, "\u2248 ", fmt.usd(sum.paid))), [...sum.unallocated.map(x => `${who(x)}: ${fmt.usd(x.net)} isn't on any budget line. Edit it to put the whole amount on its lines.`),
    ...sum.orphaned.map(x => `${who(x)}: ${fmt.usd(x.net)} is on "${x.line}", which is no longer in the budget. Delete the cost and add it again on the right line.`),
    ...(sum.noRate.length ? [`Costs in ${sum.noRate.join(', ')} have no exchange rate and are left out of the totals. Add the rate in Settings.`] : [])
  ].map((t, i) => React.createElement("div", {
    key: i, role: 'status',
    style: { fontSize: 11.5, color: T.amber, background: T.amberBg, border: `1px solid ${T.amberBr}`, padding: '6px 8px', lineHeight: 1.45 }
  }, t)))), React.createElement("div", {
    style: {
      flex: 1,
      overflowY: 'auto',
      padding: '26px 30px'
    }
  }, React.createElement(ListHead, { title: 'Costs', sub: project?.name, loading: listState === 'loading', onRefresh: loadList }), listState === 'error' && React.createElement("div", {
    style: {
      padding: '10px 12px',
      fontSize: 12,
      background: T.redBg,
      color: T.red,
      border: `1px solid ${T.redBr}`,
      marginBottom: 16
    }
  }, "Couldn't load invoices: ", listErr, ". Press Refresh to try again."), invoices.length === 0 && listState !== 'loading' ? React.createElement("div", {
    style: {
      border: `1px dashed ${T.ivoryDD}`,
      padding: '48px 24px',
      textAlign: 'center',
      fontSize: 12.5,
      color: T.mist
    }
  }, narrow ? "No costs on this budget yet. Add an invoice or a photo of a receipt above, or enter one by hand." : "No costs on this budget yet. Drop an invoice or receipt on the left, or enter one by hand.") : React.createElement("table", {
    style: {
      width: '100%',
      background: T.white,
      boxShadow: '0 1px 4px rgba(62,63,67,.06)'
    }
  }, React.createElement("thead", null, React.createElement("tr", {
    style: {
      background: T.navy
    }
  }, ['Date', 'Supplier', 'No.', 'For', 'Budget lines', 'Gross', 'Status', ''].map((h, i) => React.createElement("th", {
    key: i,
    style: {
      padding: '9px 12px',
      textAlign: h === 'Gross' ? 'right' : 'left',
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: '0.08em',
      color: 'rgba(255,255,255,.65)',
      whiteSpace: 'nowrap'
    }
  }, h)))), React.createElement("tbody", null, invoices.map((inv, i) => {
    const st = INV_ST[inv.status] || INV_ST.Received;
    const voided = inv.status === 'Void';
    return React.createElement(React.Fragment, { key: inv.id }, React.createElement("tr", {
      style: {
        background: i % 2 === 0 ? T.white : T.ivory,
        opacity: voided ? .55 : 1
      }
    }, React.createElement("td", {
      style: {
        ...MONO,
        padding: '9px 12px',
        fontSize: 11,
        color: T.mist,
        whiteSpace: 'nowrap'
      }
    }, inv.date || '—'), React.createElement("td", {
      style: {
        padding: '9px 12px',
        fontSize: 12,
        color: T.coal,
        fontWeight: 500,
        textDecoration: voided ? 'line-through' : 'none'
      }
    }, inv.supplier, inv.kind === 'receipt' && React.createElement("span", {
      style: { marginLeft: 6, padding: '1px 6px', fontSize: 9.5, fontWeight: 700, color: T.purple, background: T.purpBg, border: `1px solid ${T.purpBr}`, textDecoration: 'none', display: 'inline-block' }
    }, "RECEIPT"), inv.kind === 'receipt' && (inv.paidBy || inv.method) && React.createElement("div", {
      style: { fontSize: 10.5, color: T.mist, fontWeight: 400, marginTop: 2 }
    }, [inv.paidBy && 'Paid by ' + inv.paidBy, inv.method].filter(Boolean).join(' · '))), React.createElement("td", {
      style: {
        ...MONO,
        padding: '9px 12px',
        fontSize: 11,
        color: T.slate
      }
    }, inv.number || '—'), React.createElement("td", {
      style: {
        padding: '9px 12px',
        fontSize: 11.5,
        color: T.slate
      }
    }, inv.description || '—'), React.createElement("td", {
      style: {
        padding: '9px 12px',
        fontSize: 11,
        color: T.slate,
        lineHeight: 1.5
      }
    }, inv.allocations.map((a, j) => React.createElement("div", {
      key: j
    }, lineLabel(a), inv.allocations.length > 1 && React.createElement("span", {
      style: {
        ...MONO,
        color: T.mist
      }
    }, " \xB7 ", fmt.num(a.gross, 2))))), React.createElement("td", {
      style: {
        ...MONO,
        padding: '9px 12px',
        fontSize: 12,
        textAlign: 'right',
        color: T.coal,
        whiteSpace: 'nowrap'
      }
    }, money(Number(inv.gross), inv.ccy)), React.createElement("td", {
      style: {
        padding: '9px 12px'
      }
    }, React.createElement("select", {
      value: inv.status,
      onChange: e => changeStatus(inv, e.target.value),
      style: {
        fontSize: 10.5,
        fontWeight: 600,
        padding: '3px 6px',
        color: st.color,
        background: st.bg,
        border: `1px solid ${st.br}`,
        borderRadius: 2
      }
    }, INV_STATUSES.map(s => React.createElement("option", {
      key: s
    }, s)))), React.createElement("td", {
      style: {
        padding: '9px 12px',
        fontSize: 11,
        whiteSpace: 'nowrap'
      }
    }, safeFileUrl(inv.file) ? React.createElement("a", {
      href: safeFileUrl(inv.file),
      target: "_blank",
      rel: "noopener noreferrer",
      style: {
        color: T.navyL
      }
    }, "Open file") : React.createElement("span", {
      style: {
        color: T.ivoryDD
      }
    }, "\u2014"), canChange && React.createElement("div", {
      style: { display: 'flex', gap: 8, marginTop: 4 }
    }, React.createElement("button", {
      onClick: () => setEditing(editing === inv.id ? null : inv.id), 'aria-label': `Edit ${inv.supplier || inv.id}`, 'aria-expanded': editing === inv.id,
      style: { fontSize: 11, color: T.navyL, textDecoration: 'underline' }
    }, "Edit"), React.createElement("button", {
      onClick: () => deleteCost(inv), 'aria-label': `Delete ${inv.supplier || inv.id}`,
      style: { fontSize: 11, color: T.red, textDecoration: 'underline' }
    }, "Delete")))), editing === inv.id && React.createElement(CostEditor, {
      inv, colSpan: 8, lineLabel, onSave: invoice => saveEdit(inv, invoice), onCancel: () => setEditing(null)
    }));
  })))));
}
